// ═══════════════════════════════════════════════════════════════
// useMembership.js — verticalstack per-workspace membership lookup
// Path: src/hooks/useMembership.js
//
// S31 LAYER 1 (Apr 27, 2026) — NEW FILE for sandbox-schema migration.
//
// PURPOSE
//   Returns the current user's membership document for the currently
//   selected workspace. A "membership" is the junction record between
//   a user and a workspace — it carries the user's role(s), status,
//   and any per-parcel/per-unit scoping for that workspace.
//
//   This is what drives:
//     - ShellNav role-lock visibility (manager vs tenant vs sub views)
//     - PlaceholderDashboard role display
//     - Future: every role-scoped read in dashboard components
//
// SCHEMA (sandbox)
//   Path: clientMembers/{uid}_{workspaceId}     (composite doc ID)
//   Shape: {
//     userId, workspaceId,
//     roles: [],                   array per AS-E (Chris = ['owner_pm'])
//     status: 'active' | 'suspended' | 'invited',
//     isHomeWorkspace: bool,
//     joinedAt, addedAt, updatedAt,
//     propertyIds: [], unitIds: [],   per-scope arrays for narrow roles
//   }
//
// USAGE
//   const { membership, roles, status, loading, error } = useMembership();
//
//   - membership: full doc data (or null)
//   - roles: convenience accessor for membership.roles (or [])
//   - status: convenience accessor for membership.status (or null)
//   - loading: true while fetching
//   - error: Error object if read failed; null otherwise
//
// BEHAVIOR
//   - Re-fetches when currentUser.uid OR currentWorkspaceId changes
//   - Returns { membership: null, loading: false } if either is missing
//   - Catches permission-denied gracefully — UI handles via empty roles
//   - No side effects beyond the single read
//
// DEPENDENCIES
//   useAuth — for current user
//   useWorkspace — for current workspaceId
//   firebase/firestore — for the doc read
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';
import { useWorkspace } from './useWorkspace';

export function useMembership() {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();

  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Composite doc ID per sandbox schema: {uid}_{workspaceId}
  // (Handoff Table 2, row 3 — clientMembers/{uid}_{workspaceId})
  const membershipDocId = useMemo(() => {
    if (!user?.uid || !currentWorkspaceId) return null;
    return `${user.uid}_${currentWorkspaceId}`;
  }, [user?.uid, currentWorkspaceId]);

  useEffect(() => {
    let cancelled = false;

    async function loadMembership() {
      // No user OR no workspace selected → nothing to load
      if (!membershipDocId) {
        setMembership(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const memberRef = doc(db, 'clientMembers', membershipDocId);
        const snap = await getDoc(memberRef);

        if (cancelled) return;

        if (!snap.exists()) {
          // User isn't a member of the currently selected workspace.
          // Shouldn't happen if useWorkspace.availableWorkspaces is in
          // sync with the database, but defensive: log + return null.
          console.warn(
            `[useMembership] No membership doc for ${membershipDocId}. ` +
            'User may have been removed from this workspace, or workspace ' +
            'selection is stale. Treating as no-access.'
          );
          setMembership(null);
        } else {
          setMembership({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[useMembership] doc read failed:', err);
        setError(err);
        setMembership(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadMembership();

    return () => {
      cancelled = true;
    };
  }, [membershipDocId]);

  // Convenience accessors — never undefined, always safe defaults
  const roles = useMemo(() => {
    if (!membership || !Array.isArray(membership.roles)) return [];
    return membership.roles;
  }, [membership]);

  const status = membership?.status || null;

  return {
    membership,
    roles,
    status,
    loading,
    error,
  };
}

export default useMembership;
